// API de Salud · DIF (mcp_django apps/gov_health + MedOS apps/clinical/ai-drafts).
// Auth: JWT del dashboard. La org la decide el backend desde el JWT (nunca X-Org-Id).

import { ensureFreshToken } from '@/lib/tokenRefresh';

import type {
  AiDraft, AtencionesOut, Atencion, Calendario, CanalizacionesOut, Dictado, ExpedienteMatch, Historial, JornadaActual,
  JornadaLive, MedModelStatus, RegistroBody, ResumenServicios, Senales, Sugerencias,
} from './types';

const DJANGO_PROXY = '/django-api';
const H = '/api/v1/admin/gov-health';
const D = '/api/v1/admin/clinical/ai-drafts';

export class GovApiError extends Error {
  status: number;
  code: string;
  constructor(message: string, status: number, code = '') {
    super(message);
    this.name = 'GovApiError';
    this.status = status;
    this.code = code;
  }
}

export async function govFetch<T>(path: string, init?: RequestInit): Promise<T> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new GovApiError('Sesión expirada. Inicia sesión nuevamente.', 401);
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = typeof (err as any).detail === 'string' ? (err as any).detail : `Error ${res.status}`;
    // Solo «codigo_maquina: texto» lleva código; «canalización: destino inválido» se muestra completo.
    const m = detail.match(/^([a-z_]+):\s*(.*)$/s);
    throw new GovApiError(m ? m[2] : detail, res.status, m ? m[1] : '');
  }
  return res.json();
}

const q = (o: Record<string, string | number | undefined>) => {
  const s = new URLSearchParams();
  Object.entries(o).forEach(([k, v]) => { if (v !== undefined && v !== '') s.set(k, String(v)); });
  const t = s.toString();
  return t ? `?${t}` : '';
};

export const govHealthApi = {
  calendario: () => govFetch<Calendario>(`${H}/calendario`),
  resumen: (periodo = 'mes', zona = '') => govFetch<ResumenServicios>(`${H}/resumen-servicios${q({ periodo, zona })}`),
  senales: (zona = '') => govFetch<Senales>(`${H}/senales${q({ zona })}`),
  sugerir: (zona = '') => govFetch<Sugerencias>(`${H}/sugerir-ruta${q({ zona, cuantas: 3 })}`),
  canalizaciones: (estado = '') => govFetch<CanalizacionesOut>(`${H}/canalizaciones${q({ estado, limite: 100 })}`),
  jornada: (route_id = '') => govFetch<JornadaActual>(`${H}/jornada${q({ route_id })}`),
  atenciones: (route_id: string) => govFetch<AtencionesOut>(`${H}/jornada/${route_id}/atenciones`),
  registrar: (route_id: string, body: RegistroBody) =>
    govFetch<Atencion>(`${H}/jornada/${route_id}/atenciones`, { method: 'POST', body: JSON.stringify(body) }),
  cerrarJornada: (route_id: string) => govFetch<JornadaLive>(`${H}/jornada/${route_id}/cerrar`, { method: 'POST' }),
  dictado: (texto: string, route_id = '') => govFetch<Dictado>(`${H}/dictado`, { method: 'POST', body: JSON.stringify({ texto, route_id }) }),
  buscarExpediente: (nombre: string) => govFetch<{ items: ExpedienteMatch[] }>(`${H}/expedientes${q({ q: nombre })}`),
  historial: (patient_id: string) => govFetch<Historial>(`${H}/expedientes/${patient_id}`),
  modelStatus: () => govFetch<MedModelStatus>(`${D}/status`),
  drafts: (patient_id: string) => govFetch<{ count: number; items: AiDraft[] }>(`${D}/${q({ patient_id })}`),
  createDraft: (body: { patient_id: string; text: string; kind?: string }) =>
    govFetch<AiDraft>(`${D}/`, { method: 'POST', body: JSON.stringify({ kind: 'nota_dictado', ...body }) }),
  editDraft: (id: string, final_text: string) =>
    govFetch<AiDraft>(`${D}/${id}`, { method: 'PATCH', body: JSON.stringify({ final_text }) }),
  signDraft: (id: string, final_text: string, cedula: string) =>
    govFetch<AiDraft>(`${D}/${id}/sign`, { method: 'POST', body: JSON.stringify({ final_text, attestation: true, cedula }) }),
  discardDraft: (id: string) => govFetch<AiDraft>(`${D}/${id}/discard`, { method: 'POST' }),
  patients: () => govFetch<Array<{ id: string; display_name: string; external_id: string; age?: number | null }>>(
    '/api/v1/admin/clinical/patients?limit=50'),
};
